import { useEffect } from "react";
import { create } from "zustand";
import { useLogin } from "@privy-io/react-auth";
import { TERMS_VERSION } from "@/lib/constants";
import { useAuthStore } from "@/stores/useAuthStore";
import { useServices } from "@/services/ServiceProvider";
import type { TermsSurface } from "@/services/interfaces/IUserService";

/**
 * Records that an account saw the 18+ / Terms / Privacy notice and signed in.
 *
 * Only a *fresh* sign-in counts. A restored session never showed the notice,
 * so writing on every profile sync would credit people with accepting terms
 * they never saw. Two moments qualify:
 *
 * - The Privy modal: its global `login.onComplete` event, with
 *   `wasAlreadyAuthenticated: false`. Privy also fires it with `true` on page
 *   load for a restored session -- that one is skipped.
 * - The outreach gate (ChannelBoundLogin): headless, so it may never emit the
 *   global event. It marks its own surface just before submitting the code.
 *
 * The write waits for `profileSynced`: a brand-new account's profile row is
 * created by that first sync, and terms_acceptances references it.
 */

interface PendingTermsStore {
  pending: TermsSurface | null;
}

const usePendingTerms = create<PendingTermsStore>(() => ({ pending: null }));

/** First surface wins: the outreach gate marks itself before Privy's event can. */
export function notePendingTermsAcceptance(surface: TermsSurface): void {
  if (!usePendingTerms.getState().pending) usePendingTerms.setState({ pending: surface });
}

/** Drop a mark whose login did not complete (e.g. a wrong outreach code). */
export function clearPendingTermsAcceptance(surface: TermsSurface): void {
  if (usePendingTerms.getState().pending === surface) usePendingTerms.setState({ pending: null });
}

/** Test seam. */
export function getPendingTermsAcceptance(): TermsSurface | null {
  return usePendingTerms.getState().pending;
}

/** Mount once, inside the PrivyProvider tree (PrivyBridge). */
export function useTermsAcceptance(): void {
  useLogin({
    onComplete: ({ wasAlreadyAuthenticated }) => {
      if (!wasAlreadyAuthenticated) notePendingTermsAcceptance("privy_modal");
    },
  });

  const pending = usePendingTerms((s) => s.pending);
  const userId = useAuthStore((s) => s.session?.userId ?? null);
  const profileSynced = useAuthStore((s) => s.profileSynced);
  const { users } = useServices();

  useEffect(() => {
    if (!pending || !userId || !profileSynced) return;
    users
      .recordTermsAcceptance(userId, TERMS_VERSION, pending)
      .then(() => clearPendingTermsAcceptance(pending))
      .catch((err) => {
        // Left pending: the next profile sync for this session retries it.
        // Never blocks the user -- missing evidence is ours to fix, not theirs.
        console.error("[terms] acceptance not recorded:", err);
      });
  }, [pending, userId, profileSynced, users]);
}
